// Colour for the parts of a row's right-hand detail line ("Blocked · Social · Due 2026-06-19",
// "Mike Harrison · Still open"). The words stay the same; only the part that carries a status or a date
// takes a colour, so a glance down a list separates what is stuck, due or done. Anything unknown is
// left in the row's quiet gray, never an alarm colour.

/** Text colour per tone, for both themes. `null` keeps the row's own gray. */
export const TONE_CLASS = {
  red: 'text-red-600 dark:text-red-400',
  amber: 'text-amber-600 dark:text-amber-400',
  cyan: 'text-cyan-600 dark:text-cyan-400',
  sky: 'text-sky-600 dark:text-sky-400',
  violet: 'text-violet-600 dark:text-violet-400',
  green: 'text-emerald-600 dark:text-emerald-400',
  orange: 'text-orange-600 dark:text-orange-400',
  teal: 'text-teal-600 dark:text-teal-400',
  indigo: 'text-indigo-600 dark:text-indigo-400',
};

const STATUS_TONE = {
  blocked: 'red',
  stalled: 'amber',
  needs_assistance: 'amber',
  waiting_on_client: 'cyan',
  in_progress: 'sky',
  review: 'violet',
  in_review: 'violet',
  done: 'green',
  completed: 'green',
  complete: 'green',
  delivered: 'green',
};
const DONE = new Set(['done', 'completed', 'complete', 'delivered']);

const token = (status) => String(status ?? '').trim().toLowerCase().replace(/[\s-]+/g, '_');

/**
 * The tone of a work status, or null for one with no colour (todo, open, unknown).
 * @param {string|null|undefined} status  "in_progress", "In progress", "waiting_on_client", …
 * @returns {keyof typeof TONE_CLASS|null}
 */
export const statusTone = (status) => (Object.prototype.hasOwnProperty.call(STATUS_TONE, token(status)) ? STATUS_TONE[token(status)] : null);

/** Whether a status means the work is finished. */
export const isDoneStatus = (status) => DONE.has(token(status));

const DAY = 24 * 60 * 60 * 1000;

/**
 * The tone of a due date: red once it has passed, amber when it is due within three days (today
 * included), otherwise null. A missing or unparseable date has no tone.
 *
 * @param {string|number|Date|null|undefined} due
 * @param {Date} [now]
 * @returns {'red'|'amber'|null}
 */
export const dueTone = (due, now = new Date()) => {
  if (due == null || due === '') return null;
  const d = due instanceof Date ? due : new Date(due);
  if (Number.isNaN(d.getTime())) return null;
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  // A bare "2026-06-19" parses as UTC midnight; compare on the calendar day it names.
  const day = typeof due === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(due.trim())
    ? new Date(Number(due.slice(0, 4)), Number(due.slice(5, 7)) - 1, Number(due.slice(8, 10))).getTime()
    : new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  if (day < startOfToday) return 'red';
  if (day - startOfToday <= 3 * DAY) return 'amber';
  return null;
};

/**
 * The tone of a request's status line: delivered is green; still open is amber, and red once it
 * has waited 30 days or more.
 *
 * @param {{still_open?: boolean, days_waiting?: number|null}} request
 * @returns {'red'|'amber'|'green'}
 */
export const requestTone = (request) => {
  if (request?.still_open !== true) return 'green';
  return Number(request?.days_waiting) >= 30 ? 'red' : 'amber';
};

const CHANNEL_TONE = {
  email: 'indigo',
  chat: 'teal',
  sms: 'cyan',
  text: 'cyan',
  phone: 'green',
  voice: 'green',
  call: 'green',
};

/**
 * The tone of a conversation channel, so Email, Chat, SMS and voice read apart at a glance. Other
 * channels have no colour.
 * @param {string|null|undefined} channel
 * @returns {keyof typeof TONE_CLASS|null}
 */
export const channelTone = (channel) => {
  const k = String(channel ?? '').trim().toLowerCase();
  return Object.prototype.hasOwnProperty.call(CHANNEL_TONE, k) ? CHANNEL_TONE[k] : null;
};

/**
 * The tone of a conversation's message count, by how long the thread is: a single message is sky,
 * 2 to 4 violet, 5 to 9 amber, and 10 or more orange (a long thread worth a look). No count, no tone.
 * @param {number|string|null|undefined} count
 * @returns {'sky'|'violet'|'amber'|'orange'|null}
 */
export const messageCountTone = (count) => {
  const n = Number(count);
  if (count == null || count === '' || !Number.isFinite(n) || n < 1) return null;
  if (n === 1) return 'sky';
  if (n <= 4) return 'violet';
  if (n <= 9) return 'amber';
  return 'orange';
};

/**
 * The tone of a "last activity" date by its age in days: within a week is green (fresh), within
 * 30 days sky, within 90 days uncoloured, older amber (gone quiet). A future, missing or unparseable
 * date has no tone.
 * @param {string|number|Date|null|undefined} at
 * @param {Date} [now]
 * @returns {'green'|'sky'|'amber'|null}
 */
export const ageTone = (at, now = new Date()) => {
  if (at == null || at === '') return null;
  const d = at instanceof Date ? at : new Date(at);
  if (Number.isNaN(d.getTime())) return null;
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const day = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const age = Math.round((startOfToday - day) / DAY);
  if (age < 0) return null;
  if (age <= 7) return 'green';
  if (age <= 30) return 'sky';
  if (age <= 90) return null;
  return 'amber';
};

// Hues for event types: a mix that reads apart and never an alarm colour (red and amber stay reserved
// for "needs attention" and "overdue").
const TYPE_PALETTE = ['indigo', 'teal', 'sky', 'violet', 'cyan', 'green'];

/**
 * A stable colour for an event type. The set of types is data (it grows), so no type is listed: the
 * name is hashed onto a calm palette, which gives the same type the same colour everywhere and tells
 * different types apart, without claiming that a colour means anything.
 * @param {string|null|undefined} type
 * @returns {keyof typeof TONE_CLASS|null}
 */
export const typeTone = (type) => {
  const k = String(type ?? '').trim().toLowerCase();
  if (!k) return null;
  let h = 0;
  for (let i = 0; i < k.length; i += 1) h = (h * 31 + k.charCodeAt(i)) >>> 0;
  return TYPE_PALETTE[h % TYPE_PALETTE.length];
};

/**
 * The tone of who did something: AI and agents violet, ClientHub itself and the system teal, anyone else
 * uncoloured. Read from `actor_type` when the event carries one, else from the actor's name.
 * @param {string|null|undefined} actor
 * @param {string|null|undefined} [actorType]
 * @returns {'violet'|'teal'|null}
 */
export const actorTone = (actor, actorType) => {
  const words = `${actorType ?? ''} ${actor ?? ''}`.toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);
  if (words.some((w) => w === 'ai' || w === 'agent' || w === 'bot')) return 'violet';
  if (words.some((w) => w === 'system' || w === 'clienthub')) return 'teal';
  return null;
};
